import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { apiOrigin } from './api/client.js';
import { useMe } from './auth/useMe.js';
import { Brand } from './components/Brand.js';
import { Button, Spinner } from './components/ui.js';
import { Layout } from './components/Layout.js';
import { ThemeProvider } from './theme/ThemeProvider.js';
import { Apps } from './pages/Apps.js';
import { AppNew } from './pages/AppNew.js';
import { AppAgent } from './pages/AppAgent.js';
import { AppDetail } from './pages/AppDetail.js';
import { Setup } from './pages/Setup.js';
import { Versions } from './pages/Versions.js';
import { Share } from './pages/Share.js';
import { Settings } from './pages/Settings.js';
import { Secrets } from './pages/Secrets.js';
import { Connections } from './pages/Connections.js';
import { Dependencies } from './pages/Dependencies.js';
import { Schedules } from './pages/Schedules.js';
import { Tokens } from './pages/Tokens.js';
import { Health } from './pages/Health.js';
import { Activity } from './pages/Activity.js';
import { Usage } from './pages/Usage.js';
import { AdminUsers } from './pages/admin/AdminUsers.js';
import { AdminApps } from './pages/admin/AdminApps.js';
import { AdminAppDetail } from './pages/admin/AdminAppDetail.js';
import { AdminActivity } from './pages/admin/AdminActivity.js';
import { AdminIntegrations } from './pages/admin/AdminIntegrations.js';
import { AdminSettings } from './pages/admin/AdminSettings.js';
import { AdminTokens } from './pages/admin/AdminTokens.js';
import { AdminUsage } from './pages/admin/AdminUsage.js';
import { AdminAgentUsage } from './pages/admin/AdminAgentUsage.js';
import { DocsIntro } from './pages/docs/DocsIntro.js';
import { DocsUsingApps } from './pages/docs/DocsUsingApps.js';
import { DocsGettingStarted } from './pages/docs/DocsGettingStarted.js';
import { DocsFirstApp } from './pages/docs/DocsFirstApp.js';
import { DocsPrompting } from './pages/docs/DocsPrompting.js';
import { DocsExamples } from './pages/docs/DocsExamples.js';
import { DocsConnections } from './pages/docs/DocsConnections.js';
import { DocsFaq } from './pages/docs/DocsFaq.js';

// Code-split — the editor pulls in Monaco (~3 MB) and only managers
// reach it. Non-editor portal pages stay snappy.
const AppEditor = lazy(() =>
  import('./pages/AppEditor.js').then((m) => ({ default: m.AppEditor })),
);

function CenteredShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full flex flex-col items-center justify-center gap-6 px-4 text-center">
      <Brand height={42} />
      {children}
    </div>
  );
}

function LoadingShell() {
  return (
    <CenteredShell>
      <div className="flex items-center gap-2 text-muted text-sm">
        <Spinner />
        Loading…
      </div>
    </CenteredShell>
  );
}

function authLoginUrl(): string {
  // Top-level navigation bounces the browser to the auth-worker, which
  // runs the Google OIDC flow and sets `kin_session` scoped to `.{base}`
  // before redirecting back here. Per-subdomain Access cookie warmup is
  // gone — one cookie covers Portal and API.
  //
  // The Portal IS the env's base domain (Portal at `dev.mkin.app` → auth
  // at `auth.dev.mkin.app`; Portal at `mkin.app` → auth at `auth.mkin.app`),
  // so the auth host is just `auth.${window.location.hostname}`. No
  // segment stripping — stripping treats `dev.mkin.app` as a sub-subdomain
  // and sends the user to the production auth host `auth.mkin.app`.
  const ret = encodeURIComponent(window.location.href);
  return `https://auth.${window.location.hostname}/login?return_to=${ret}`;
}

function UnauthenticatedShell() {
  useEffect(() => {
    const guard = sessionStorage.getItem('kin_auth_login_attempted');
    if (guard === window.location.href) return;
    sessionStorage.setItem('kin_auth_login_attempted', window.location.href);
    window.location.href = authLoginUrl();
  }, []);
  return (
    <CenteredShell>
      <div className="space-y-2 max-w-sm">
        <h1 className="font-display text-xl font-semibold">Signing you in…</h1>
        <p className="text-muted text-sm">
          Redirecting to the Kin sign-in. If this page sticks, click below.
        </p>
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={() => {
          sessionStorage.removeItem('kin_auth_login_attempted');
          window.location.href = authLoginUrl();
        }}
      >
        Continue
      </Button>
    </CenteredShell>
  );
}

function ErrorShell({ message }: { message: string }) {
  return (
    <CenteredShell>
      <div className="space-y-2 max-w-sm">
        <h1 className="font-display text-xl font-semibold">Couldn&apos;t reach Kin</h1>
        <p className="text-muted text-sm">{message}</p>
      </div>
    </CenteredShell>
  );
}

function NotFound() {
  return (
    <div className="text-center py-20 animate-fade-in">
      <p className="text-xs uppercase tracking-wider text-muted mb-2">404</p>
      <h1 className="font-display text-3xl font-semibold mb-2">Page not found</h1>
      <p className="text-muted text-sm">That page doesn&apos;t exist yet.</p>
    </div>
  );
}

function AppRoutes() {
  const auth = useMe();

  useEffect(() => {
    if (auth.state === 'authenticated') {
      sessionStorage.removeItem('kin_auth_login_attempted');
    }
  }, [auth.state]);

  if (auth.state === 'loading') return <LoadingShell />;
  if (auth.state === 'unauthenticated') return <UnauthenticatedShell />;
  if (auth.state === 'error')
    return <ErrorShell message={auth.error?.message ?? 'Unknown error'} />;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout me={auth.me!} />}>
          <Route index element={<Apps me={auth.me!} />} />
          <Route path="/apps/new" element={<AppNew me={auth.me!} />} />
          <Route path="/apps/:slug" element={<AppDetail />} />
          <Route path="/apps/:slug/agent" element={<AppAgent />} />
          <Route path="/apps/:slug/share" element={<Share />} />
          <Route path="/apps/:slug/versions" element={<Versions />} />
          <Route path="/apps/:slug/settings" element={<Settings />} />
          <Route path="/apps/:slug/secrets" element={<Secrets />} />
          <Route path="/apps/:slug/connections" element={<Connections />} />
          <Route path="/apps/:slug/dependencies" element={<Dependencies />} />
          <Route path="/apps/:slug/schedules" element={<Schedules />} />
          <Route path="/apps/:slug/tokens" element={<Tokens />} />
          <Route path="/apps/:slug/health" element={<Health />} />
          <Route path="/apps/:slug/activity" element={<Activity />} />
          <Route path="/apps/:slug/usage" element={<Usage />} />
          <Route
            path="/apps/:slug/editor"
            element={
              <Suspense fallback={<LoadingShell />}>
                <AppEditor />
              </Suspense>
            }
          />
          <Route path="/setup" element={<Setup />} />
          <Route path="/mcp" element={<Navigate to="/setup" replace />} />
          <Route path="/docs" element={<DocsIntro />} />
          <Route path="/docs/using-apps" element={<DocsUsingApps />} />
          <Route path="/docs/getting-started" element={<DocsGettingStarted />} />
          <Route path="/docs/first-app" element={<DocsFirstApp />} />
          <Route path="/docs/prompting" element={<DocsPrompting />} />
          <Route path="/docs/examples" element={<DocsExamples />} />
          <Route path="/docs/connections" element={<DocsConnections />} />
          <Route path="/docs/faq" element={<DocsFaq />} />
          {auth.me!.globalRole === 'platform_admin' && (
            <>
              <Route path="/admin" element={<AdminUsers />} />
              <Route path="/admin/apps" element={<AdminApps />} />
              <Route path="/admin/apps/:slug" element={<AdminAppDetail />} />
              <Route path="/admin/activity" element={<AdminActivity />} />
              <Route path="/admin/tokens" element={<AdminTokens />} />
              <Route path="/admin/integrations" element={<AdminIntegrations />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/usage" element={<AdminUsage />} />
              <Route path="/admin/agent-usage" element={<AdminAgentUsage />} />
            </>
          )}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <AppRoutes />
    </ThemeProvider>
  );
}
